import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

// Counts the sentinel as in view a little before it reaches the bottom of the root. Without it, a zero-height sentinel
// flush with that edge (a list too short to scroll) can round to just outside the root and never report as in view.
const ROOT_MARGIN = '0px 0px 100px 0px';

type IUseInfiniteScrollOptions = {
  rootRef: RefObject<HTMLElement | null>;
  hasNextPage: boolean;
  onLoadMore: () => Promise<unknown>;
};

export const useInfiniteScroll = ({ rootRef, hasNextPage, onLoadMore }: IUseInfiniteScrollOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const hasNextPageRef = useRef(hasNextPage);
  const isLoadingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Synced after commit, not during render: a transition render that is still pending (e.g. merging a fetched page)
  // must not hand the observer its not-yet-shown state, or it would request the page after the one being merged.
  useLayoutEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    hasNextPageRef.current = hasNextPage;
  });

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || !hasNextPageRef.current || isLoadingMoreRef.current) return;

        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
        void onLoadMoreRef
          .current()
          .then(
            () => {
              // The observer only reports changes, so a page that leaves the sentinel in view (a short or mostly
              // filtered page) would otherwise end loading. Re-observing makes it report the post-load state afresh.
              observer.unobserve(sentinel);
              observer.observe(sentinel);
            },
            // Reporting a failed load is the caller's job (inside onLoadMore); here it only has to unlock retries,
            // not re-trigger them, or a persistent error would loop.
            () => undefined,
          )
          .finally(() => {
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
          });
      },
      { root: rootRef.current, rootMargin: ROOT_MARGIN },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [rootRef]);

  return { sentinelRef, isLoadingMore };
};
